import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  InputAdornment,
  IconButton,
  Alert,
  LinearProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
} from "@mui/material";
import { Visibility, VisibilityOff, Business, LocationOn, Description, AccountCircle, CheckCircle, Upload, Lock } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import api from "../../api/axios";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../../utils/SnackbarProvider";
import MapPicker from "./MapPicker";

// Step definitions
const STEPS = [
  { label: 'Account', icon: <AccountCircle /> },
  { label: 'Business', icon: <Business /> },
  { label: 'Details', icon: <Description /> },
  { label: 'Address', icon: <LocationOn /> },
];

// Password Strength Function
const getPasswordStrength = (password) => {
  let score = 0;
  if (!password) return 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score;
};

const strengthLabel = ["Very Weak", "Weak", "Moderate", "Strong", "Very Strong"];
const strengthColor = ["#D32F2F", "#F57C00", "#FBC02D", "#388E3C", "#2E7D32"];

// Required field indicator
const RequiredLabel = ({ children }) => (
  <span>{children} <span style={{ color: "#ff0000" }}>*</span></span>
);

// Modern button style (matching VendorRegistration)
const modernButtonStyle = {
  borderRadius: 2,
  textTransform: 'none',
  fontWeight: 600,
  px: 3,
  py: 1,
  transition: 'all 0.2s ease',
  boxShadow: '0 2px 8px rgba(255,153,0,0.2)',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(255,153,0,0.3)',
  },
};

// Combined agent schema with all fields (vendor pattern — single schema for reset() compatibility)
const agentSchema = z.object({
  // Step 0: Account
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Invalid email"),
  mobile: z.string().min(8, "Mobile number required"),
  password: z.string().optional(),
  confirmPassword: z.string().optional(),
  // Step 1: Business
  preferredLanguage: z.string().optional(),
  agentType: z.string().optional(),
  areaOfOperation: z.string().optional(),
  legalName: z.string().optional(),
  businessRegNumber: z.string().optional(),
  incorporationDate: z.string().optional(),
  // Step 2: Contact
  contactPerson: z.string().optional(),
  designation: z.string().optional(),
  contactPhone: z.string().optional(),
  contactEmail: z.string().optional().or(z.literal("")),
  altContact: z.string().optional(),
  // Step 3: Address (same fields as vendor)
  addressLine1: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  pincode: z.string().optional(),
  businessAddress: z.string().optional(),
  website: z.string().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
}).refine((data) => {
  if (!data.password) return true;
  return data.password === data.confirmPassword;
}, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

// Per-step validation schemas (used for manual validation in handleNext only, not as form resolver)
const stepSchemas = {
  0: z.object({
    name: z.string().min(2, "Name is required"),
    email: z.string().email("Invalid email"),
    mobile: z.string().min(8, "Mobile number required"),
  }),
  1: z.object({
    preferredLanguage: z.string().min(1, "Required"),
    agentType: z.string().min(1, "Required"),
    areaOfOperation: z.string().min(1, "Area of operation is required"),
    legalName: z.string().min(2, "Required"),
    businessRegNumber: z.string().min(2, "Required"),
    incorporationDate: z.string().min(1, "Required"),
  }),
  2: z.object({
    contactPerson: z.string().min(2, "Required"),
    designation: z.string().min(2, "Required"),
    contactPhone: z.string().min(8, "Required"),
    contactEmail: z.string().min(1, "Required").email("Invalid email"),
    altContact: z.string().optional(),
  }),
  3: z.object({
    addressLine1: z.string().min(5, "Street address is required"),
    city: z.string().min(2, "City is required"),
    state: z.string().min(2, "State is required"),
    pincode: z.string().min(4, "Pincode is required"),
    businessAddress: z.string().optional(),
    website: z.string().optional(),
  }),
};

const AgentRegistration = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Change Password Dialog
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirmNew, setShowConfirmNew] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, color: "#eee" });

  // OTP state
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);

  // Password strength handler
  const evaluatePasswordStrength = (pwd) => {
    let score = 0;
    if (!pwd) return { score: 0, color: "#eee" };
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    const colors = ["#D32F2F", "#F57C00", "#FBC02D", "#388E3C", "#2E7D32"];
    return { score, color: colors[score] || "#2E7D32" };
  };

  // Password change schema
  const PasswordSchema = z.object({
    currentPassword: z.string().min(6, "Minimum 6 characters"),
    newPassword: z.string().min(6, "Minimum 6 characters"),
    confirmNewPassword: z.string().min(6, "Minimum 6 characters"),
  }).refine(data => data.newPassword === data.confirmNewPassword, {
    message: "Passwords do not match",
    path: ["confirmNewPassword"],
  });

  // Separate form for password change dialog
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPasswordForm,
    formState: { errors: passwordErrors },
  } = useForm({
    resolver: zodResolver(PasswordSchema),
    mode: "onBlur",
  });

  // Profile image upload
  const [profilePic, setProfilePic] = useState("");
  const [profilePicFile, setProfilePicFile] = useState(null);
  const profileInputRef = useRef(null);

  // Business license upload
  const [businessLicense, setBusinessLicense] = useState(null);
  const [businessLicensePreview, setBusinessLicensePreview] = useState("");

  // User email (for OTP in edit mode)
  const [userEmail, setUserEmail] = useState("");

  const {
    register,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(agentSchema),
    mode: "onBlur",
    defaultValues: {
      name: "",
      email: "",
      mobile: "",
      password: "",
      confirmPassword: "",
      preferredLanguage: "",
      agentType: "",
      areaOfOperation: "",
      legalName: "",
      businessRegNumber: "",
      incorporationDate: "",
      contactPerson: "",
      designation: "",
      contactPhone: "",
      contactEmail: "",
      altContact: "",
      addressLine1: "",
      city: "",
      state: "",
      pincode: "",
      businessAddress: "",
      website: "",
      latitude: null,
      longitude: null,
    },
  });

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    return parts.length > 1
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name[0].toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = ["#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7", "#DDA0DD", "#98D8C8", "#F7DC6F", "#BB8FCE", "#85C1E9"];
    let hash = 0;
    if (name) {
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const handleProfileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfilePicFile(file);
      setProfilePic(URL.createObjectURL(file));
    }
  };

  const password = watch("password", "");

  // initialLocation for map picker — reactive to location changes (auto-geocode)
  const location = watch("location");
  const initialLocation = useMemo(() => {
    if (
      location?.coordinates &&
      location.coordinates.length === 2 &&
      location.coordinates[0] !== 0 &&
      location.coordinates[1] !== 0
    ) {
      return {
        latitude: location.coordinates[1],
        longitude: location.coordinates[0],
      };
    }
    return { lat: 0, lng: 0, address: "" };
  }, [location]);

  // Load existing profile (edit mode)
  const loadProfile = async () => {
    let userData = null;
    try {
      const res = await api.get("/agent/profile");
      const { user, profile } = res.data;

      if (profile) {
        userData = user;
        setIsEditMode(true);
        setUserEmail(user?.email || "");
        setOtpVerified(true);

        reset({
          name: user?.name || "",
          email: user?.email || "",
          mobile: user?.mobile || "",
          preferredLanguage: profile.preferredLanguage || "",
          agentType: profile.agentType || "",
          areaOfOperation: profile.areaOfOperation || "",
          legalName: profile.legalName || "",
          businessRegNumber: profile.businessRegNumber || "",
          incorporationDate: profile.incorporationDate
            ? new Date(profile.incorporationDate).toISOString().slice(0, 10)
            : "",
          contactPerson: profile.contactPerson || "",
          designation: profile.designation || "",
          contactPhone: profile.contactPhone || "",
          contactEmail: profile.contactEmail || "",
          altContact: profile.altContact || "",
          addressLine1: profile.addressLine1 || "",
          city: profile.city || "",
          state: profile.state || "",
          pincode: profile.pincode || "",
          businessAddress: profile.businessAddress || "",
          website: profile.website || "",
          latitude: profile.location?.latitude || null,
          longitude: profile.location?.longitude || null,
        });

        // Profile image
        if (profile.profileImage) {
          setProfilePic(profile.profileImage);
        }

        // Business license
        if (profile.businessLicense) {
          setBusinessLicense({ name: "business_license", type: "image" });
          // Only render as image if the stored value is a valid renderable URL
          if (
            profile.businessLicense.startsWith('data:image') ||
            profile.businessLicense.startsWith('http://') ||
            profile.businessLicense.startsWith('https://') ||
            profile.businessLicense.startsWith('blob:')
          ) {
            setBusinessLicensePreview(profile.businessLicense);
          }
        }
      } else if (user) {
        userData = user;
        setUserEmail(user.email || "");
        reset({
          name: user.name || "",
          email: user.email || "",
          mobile: user.mobile || "",
        });
      }
    } catch (err) {
      // Not logged in or no profile yet — stay in registration mode
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // OTP timer
  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => setOtpTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  /* ============================
     AUTO GEOCODE ADDRESS
     ============================ */
  const geocodeAddress = async (address) => {
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

      const res = await axios.get(
        "https://maps.googleapis.com/maps/api/geocode/json",
        {
          params: {
            address,
            key: apiKey,
          },
        }
      );

      if (res.data.results.length > 0) {
        const loc = res.data.results[0].geometry.location;

        return {
          type: "Point",
          coordinates: [loc.lng, loc.lat],
        };
      }

      return null;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  useEffect(() => {
    const timer = setTimeout(async () => {
      const address = [
        watch("addressLine1"),
        watch("city"),
        watch("state"),
        watch("pincode"),
        "India",
      ]
        .filter(Boolean)
        .join(", ");

      if (address.length > 10) {
        const geo = await geocodeAddress(address);

        if (geo) {
          setValue("location", geo);
        }
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [
    watch("addressLine1"),
    watch("city"),
    watch("state"),
    watch("pincode"),
  ]);

  const createUserForOTP = async (mobile, email) => {
    try {
      await api.post("/auth/login", {
        identifier: mobile,
        role: "agent",
        email,
        name: getValues("name"),
        loginLocation: { latitude: null, longitude: null },
      });
    } catch (err) {
      // User may already exist — that's fine
    }
  };

  const handleSendOTP = async () => {
    const mobile = getValues("mobile");
    const email = getValues("email");
    const name = getValues("name");
    if (!mobile || !email || !name) {
      setOtpError("Please fill name, email and mobile first");
      return;
    }
    setOtpLoading(true);
    setOtpError("");
    try {
      await createUserForOTP(mobile, email);
      await api.post("/users/send-otp", { identifier: mobile });
      setOtpSent(true);
      setOtpTimer(30);
      showSnackbar("OTP sent to your WhatsApp", "success");
    } catch (err) {
      setOtpError(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length < 4) {
      setOtpError("Enter a valid 4-digit OTP");
      return;
    }
    setOtpLoading(true);
    setOtpError("");
    try {
      const res = await api.post("/users/verify-otp", {
        identifier: getValues("mobile"),
        otp,
      });
      if (res.data?.verified) {
        setOtpVerified(true);
        showSnackbar("Mobile verified!", "success");
      } else {
        setOtpError("Invalid OTP. Try again.");
      }
    } catch (err) {
      setOtpError(err.response?.data?.message || "Verification failed");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleStepClick = (step) => {
    if (step < activeStep) {
      setActiveStep(step);
    }
  };

  const handleNext = (e) => {
    if (e) e.preventDefault();
    const stepSchema = stepSchemas[activeStep];
    if (!stepSchema) {
      setActiveStep((prev) => prev + 1);
      return;
    }
    const currentData = getValues();
    const result = stepSchema.safeParse(currentData);
    if (result.success) {
      setActiveStep((prev) => prev + 1);
    } else {
      const firstError = result.error.issues[0];
      showSnackbar(firstError?.message || "Please fill all required fields", "error");
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(0, prev - 1));
  };

  const handlePasswordChange = async (data) => {
    try {
      await api.put("/auth/change-password", {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      showSnackbar("Password changed successfully!", "success");
      setOpenPasswordDialog(false);
      resetPasswordForm();
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Failed to change password", "error");
    }
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    try {
      // If new user, create login first
      if (!isEditMode) {
        await api.post("/auth/login", {
          identifier: getValues("mobile"),
          role: "agent",
          email: getValues("email"),
          name: getValues("name"),
          loginLocation: { latitude: null, longitude: null },
        });
      }

      // Update user profile (name, email)
      if (!isEditMode) {
        await api.put("/users/profile", {
          name: getValues("name"),
          email: getValues("email"),
        });
      }

      // Build FormData for agent profile
      const fd = new FormData();
      const profileFields = [
        "legalName", "businessRegNumber", "incorporationDate",
        "agentType", "areaOfOperation", "preferredLanguage",
        "contactPerson", "designation", "contactPhone", "contactEmail",
        "altContact",
        "addressLine1", "city", "state", "pincode",
        "businessAddress", "website",
      ];
      profileFields.forEach((f) => {
        const v = getValues(f);
        if (v !== undefined && v !== null && v !== "") {
          fd.append(f, v);
        }
      });

      const lat = getValues("latitude");
      const lng = getValues("longitude");
      if (lat != null) fd.append("latitude", lat);
      if (lng != null) fd.append("longitude", lng);

      if (profilePicFile) {
        fd.append("profileImage", profilePicFile);
      }
      if (businessLicense instanceof File) {
        fd.append("businessLicense", businessLicense);
      }

      await api.put("/agent/profile", fd);
      showSnackbar(isEditMode ? "Profile updated successfully!" : "Agent registration completed!", "success");

      navigate("/agent/dashboard");
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Failed to save profile", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setBusinessLicense(file);
      if (file.type.startsWith("image/")) {
        setBusinessLicensePreview(URL.createObjectURL(file));
      } else {
        setBusinessLicensePreview("");
      }
    } else {
      setBusinessLicense(null);
      setBusinessLicensePreview("");
    }
  };

  // Show loading while checking profile
  if (initialLoading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress sx={{ color: '#ff9900' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", pt: 2, pb: 4, px: 1, bgcolor: "#f8f9fa" }}>
      <Box sx={{ maxWidth: 900, mx: 'auto', mb: 2, px: 1 }}>
        <Paper sx={{ p: { xs: 1, sm: 2 }, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ '& .MuiStepLabel-label': { fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.875rem' } } }}>
            {STEPS.map((step, index) => (
              <Step key={step.label} sx={{ cursor: 'pointer' }} onClick={() => handleStepClick(index)}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      '&.Mui-active': { color: '#ff9900' },
                      '&.Mui-completed': { color: '#4caf50' },
                      fontSize: { xs: '1.2rem', sm: '1.5rem' },
                    },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{step.label}</Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 4 }, maxWidth: 900, mx: "auto", borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Box sx={{ textAlign: 'center', mb: 3, pb: 2, borderBottom: '2px solid #f0f0f0' }}>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#333', mb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            {isEditMode ? "Agent Profile" : "Agent Registration"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            {isEditMode ? "Update your agent profile information" : "Complete all steps to create your agent account"}
          </Typography>
          <Box sx={{ mt: 2, display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.5, bgcolor: '#fff3e0', borderRadius: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Typography variant="body2" fontWeight={600} sx={{ color: '#e65100', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Step {activeStep + 1} of {STEPS.length}:
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              {STEPS[activeStep]?.label}
            </Typography>
          </Box>
        </Box>

        <form>
          {activeStep === 0 && (
            <Fade in>
              <Box>
                {/* Profile Image Upload */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Box sx={{ display: 'inline-block', position: 'relative' }}>
                    <Avatar
                      src={profilePic}
                      sx={{
                        width: { xs: 80, sm: 100 },
                        height: { xs: 80, sm: 100 },
                        fontSize: { xs: 32, sm: 40 },
                        fontWeight: 700,
                        bgcolor: getAvatarColor(watch("name")),
                        border: '3px solid #ff9900',
                        boxShadow: '0 4px 16px rgba(255,153,0,0.2)',
                      }}
                    >
                      {getInitials(watch("name"))}
                    </Avatar>
                    <IconButton
                      size="small"
                      onClick={() => profileInputRef.current?.click()}
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        bgcolor: '#ff9900',
                        color: '#fff',
                        '&:hover': { bgcolor: '#e68a00' },
                        boxShadow: '0 2px 8px rgba(255,153,0,0.4)',
                      }}
                    >
                      <Upload fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                    Click to upload profile photo
                  </Typography>
                  <input
                    type="file"
                    hidden
                    ref={profileInputRef}
                    accept="image/*"
                    onChange={handleProfileUpload}
                  />
                </Box>

                {/* Change Password Button - Edit Mode Only */}
                {isEditMode && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                      startIcon={<Lock />}
                      onClick={() => setOpenPasswordDialog(true)}
                      variant="text"
                      size="small"
                      sx={{ color: '#666' }}
                    >
                      Change Password
                    </Button>
                  </Box>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  {/* Name */}
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                      <RequiredLabel>Name</RequiredLabel>
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      {...register("name")}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  </Box>

                  {/* Email */}
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                      <RequiredLabel>Email</RequiredLabel>
                    </Typography>
                    <TextField
                      type="email"
                      fullWidth
                      size="small"
                      {...register("email")}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  </Box>

                  {/* Mobile */}
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                      <RequiredLabel>Mobile Number</RequiredLabel>
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      {...register("mobile")}
                      error={!!errors.mobile}
                      helperText={errors.mobile?.message}
                      disabled={otpVerified}
                    />
                  </Box>

                  {/* OTP Section */}
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                      Mobile Verification
                    </Typography>
                    {!otpVerified ? (
                      <Box>
                        {otpSent ? (
                          <Box sx={{ p: 2, bgcolor: '#fff8e1', borderRadius: 2, border: '1px solid #ffecb3' }}>
                            <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#f57c00' }}>
                              Enter the 4-digit code sent to your WhatsApp
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                              <TextField
                                label="OTP Code"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                placeholder="4-digit"
                                size="small"
                                sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}
                              />
                              <Button
                                variant="contained"
                                onClick={handleVerifyOTP}
                                disabled={otpLoading}
                                sx={{ ...modernButtonStyle, bgcolor: '#ff9900', width: { xs: '100%', sm: 'auto' } }}
                              >
                                {otpLoading ? <CircularProgress size={16} /> : "Verify"}
                              </Button>
                            </Box>
                            {otpError && <Alert severity="error" sx={{ mt: 1 }}>{otpError}</Alert>}
                            <Box sx={{ mt: 1 }}>
                              <Button
                                size="small"
                                onClick={handleSendOTP}
                                disabled={otpTimer > 0 || otpLoading}
                                sx={{ color: '#666' }}
                              >
                                {otpTimer > 0 ? `Resend in ${otpTimer}s` : "Resend OTP"}
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={handleSendOTP}
                            disabled={otpLoading}
                            sx={{ ...modernButtonStyle, bgcolor: otpTimer > 0 ? '#ccc' : '#ff9900', whiteSpace: 'nowrap', width: { xs: '100%', sm: 'auto' } }}
                          >
                            {otpLoading ? <CircularProgress size={20} /> : "Send OTP to WhatsApp"}
                          </Button>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2, py: 1, bgcolor: '#e8f5e9', borderRadius: 2, width: 'fit-content' }}>
                        <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>Verified</Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Password - Only show when NOT in edit mode */}
                  {!isEditMode && (
                    <>
                      <Box>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                          <RequiredLabel>Password</RequiredLabel>
                        </Typography>
                        <TextField
                          type={showPassword ? "text" : "password"}
                          fullWidth
                          size="small"
                          {...register("password")}
                          error={!!errors.password}
                          helperText={errors.password?.message || "At least 8 characters"}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>

                      {/* Confirm Password */}
                      <Box>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                          <RequiredLabel>Confirm Password</RequiredLabel>
                        </Typography>
                        <TextField
                          type={showConfirmPassword ? "text" : "password"}
                          fullWidth
                          size="small"
                          {...register("confirmPassword")}
                          error={!!errors.confirmPassword}
                          helperText={errors.confirmPassword?.message}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small">
                                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>

                      {/* Password Strength */}
                      {password && (
                        <Box sx={{ gridColumn: { md: '1 / -1' }, mt: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={(getPasswordStrength(password) / 4) * 100}
                              sx={{
                                flex: 1,
                                height: 6,
                                borderRadius: 2,
                                backgroundColor: "#eee",
                                "& .MuiLinearProgress-bar": { backgroundColor: strengthColor[getPasswordStrength(password)] },
                              }}
                            />
                            <Typography variant="caption" sx={{ color: strengthColor[getPasswordStrength(password)], minWidth: 70 }}>
                              {strengthLabel[getPasswordStrength(password)]}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Fade>
          )}

          {activeStep === 1 && (
            <Fade in>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Preferred Language</RequiredLabel>
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={watch("preferredLanguage") || ""}
                    {...register("preferredLanguage")}
                    error={!!errors.preferredLanguage}
                  >
                    <MenuItem value="English">English</MenuItem>
                    <MenuItem value="Mandarin">Mandarin</MenuItem>
                    <MenuItem value="Cantonese">Cantonese</MenuItem>
                  </TextField>
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Agent Type</RequiredLabel>
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={watch("agentType") || ""}
                    {...register("agentType")}
                    error={!!errors.agentType}
                  >
                    <MenuItem value="Individual">Individual</MenuItem>
                    <MenuItem value="Company">Company</MenuItem>
                    <MenuItem value="Partnership">Partnership</MenuItem>
                  </TextField>
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Area of Operation</RequiredLabel>
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={watch("areaOfOperation") || ""}
                    {...register("areaOfOperation")}
                    error={!!errors.areaOfOperation}
                  >
                    <MenuItem value="India">India</MenuItem>
                    <MenuItem value="China">China</MenuItem>
                    <MenuItem value="Both">Both (India & China)</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </TextField>
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Legal Name</RequiredLabel>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("legalName")}
                    error={!!errors.legalName}
                    helperText={errors.legalName?.message}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Business Reg Number</RequiredLabel>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("businessRegNumber")}
                    error={!!errors.businessRegNumber}
                    helperText={errors.businessRegNumber?.message}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Incorporation Date</RequiredLabel>
                  </Typography>
                  <TextField
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    {...register("incorporationDate")}
                    error={!!errors.incorporationDate}
                    helperText={errors.incorporationDate?.message}
                  />
                </Box>
              </Box>
            </Fade>
          )}

          {activeStep === 2 && (
            <Fade in>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Contact Person</RequiredLabel>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("contactPerson")}
                    error={!!errors.contactPerson}
                    helperText={errors.contactPerson?.message}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Designation</RequiredLabel>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("designation")}
                    error={!!errors.designation}
                    helperText={errors.designation?.message}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Contact Phone</RequiredLabel>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("contactPhone")}
                    error={!!errors.contactPhone}
                    helperText={errors.contactPhone?.message}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    Contact Email
                  </Typography>
                  <TextField
                    type="email"
                    fullWidth
                    size="small"
                    {...register("contactEmail")}
                    error={!!errors.contactEmail}
                    helperText={errors.contactEmail?.message}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    Alternative Contact
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("altContact")}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    Business License (Optional)
                  </Typography>
                  <Button variant="outlined" size="small" component="label">
                    Upload License
                    <input type="file" hidden accept=".pdf,.jpg,.png" onChange={handleFileChange} />
                  </Button>
                  {businessLicense && (
                    <Box sx={{ mt: 1 }}>
                      {businessLicensePreview ? (
                        <Box
                          component="img"
                          src={businessLicensePreview}
                          alt="Business License"
                          sx={{
                            width: 120,
                            height: 80,
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            cursor: 'pointer',
                          }}
                          onClick={() => window.open(businessLicensePreview, '_blank')}
                        />
                      ) : (
                        <Typography variant="caption" sx={{ mt: 0.5, color: '#666', display: 'block' }}>
                          {businessLicense.name}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Fade>
          )}

          {activeStep === 3 && (
            <Fade in>
              <Box>
                {/* Map Picker - Full Width */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1.5, color: '#555' }}>
                    📍 Business Location
                  </Typography>
                  <MapPicker
                    setValue={setValue}
                    initialLocation={initialLocation}
                  />
                </Box>

                {/* Street Address */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Street Address</RequiredLabel>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("addressLine1")}
                    error={!!errors.addressLine1}
                    helperText={errors.addressLine1?.message || "Enter your business street address"}
                    InputLabelProps={{ shrink: true }}
                    placeholder="Enter your business address"
                  />
                </Box>

                {/* City & State Row */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                      <RequiredLabel>City</RequiredLabel>
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      {...register("city")}
                      error={!!errors.city}
                      helperText={errors.city?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="City"
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                      <RequiredLabel>State</RequiredLabel>
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      {...register("state")}
                      error={!!errors.state}
                      helperText={errors.state?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="State"
                    />
                  </Box>
                </Box>

                {/* Pincode & Website Row */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                      <RequiredLabel>Pincode</RequiredLabel>
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      {...register("pincode")}
                      error={!!errors.pincode}
                      helperText={errors.pincode?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Postal code"
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                      Website (Optional)
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      {...register("website")}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                </Box>

                {/* Hidden lat/lng fields for form submission */}
                <input type="hidden" {...register("latitude")} />
                <input type="hidden" {...register("longitude")} />
              </Box>
            </Fade>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              type="button"
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0}
              sx={{ ...modernButtonStyle, borderColor: '#ff9900', color: '#ff9900', width: { xs: '100%', sm: 'auto' } }}
            >
              Back
            </Button>

            {activeStep >= STEPS.length - 1 ? (
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                onClick={(e) => { e.preventDefault(); handleFinalSubmit(); }}
                sx={{ ...modernButtonStyle, bgcolor: '#4caf50', width: { xs: '100%', sm: 'auto' } }}
              >
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            ) : (
              <Button
                type="button"
                variant="contained"
                onClick={handleNext}
                disabled={activeStep >= STEPS.length - 1}
                sx={{ ...modernButtonStyle, bgcolor: "#ff9900", width: { xs: '100%', sm: 'auto' } }}
              >
                Next
              </Button>
            )}
          </Box>
        </form>

        <Typography variant="body2" sx={{ mt: 3, textAlign: "center", color: "#666" }}>
          By registering, you agree to Project X's Terms of Service and Privacy Policy.
        </Typography>
      </Paper>

      {/* Change Password Dialog */}
      <Dialog
        open={openPasswordDialog}
        onClose={() => setOpenPasswordDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
            <Lock sx={{ color: '#ff9900' }} />
            <Typography variant="h6" fontWeight={600}>Change Password</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Current Password"
            type={showCurrent ? "text" : "password"}
            margin="normal"
            variant="outlined"
            {...registerPassword("currentPassword")}
            error={!!passwordErrors.currentPassword}
            helperText={passwordErrors.currentPassword?.message}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowCurrent(!showCurrent)}>
                    {showCurrent ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="New Password"
            type={showNew ? "text" : "password"}
            margin="normal"
            variant="outlined"
            {...registerPassword("newPassword")}
            error={!!passwordErrors.newPassword}
            helperText={passwordErrors.newPassword?.message}
            onChange={(e) => {
              registerPassword("newPassword").onChange(e);
              setPasswordStrength(evaluatePasswordStrength(e.target.value));
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNew(!showNew)}>
                    {showNew ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <LinearProgress
            variant="determinate"
            value={(passwordStrength.score / 4) * 100}
            sx={{
              height: 8,
              borderRadius: 4,
              my: 1,
              bgcolor: "#eee",
              "& .MuiLinearProgress-bar": {
                bgcolor: passwordStrength.color,
                borderRadius: 4,
              },
            }}
          />

          <TextField
            fullWidth
            label="Confirm New Password"
            type={showConfirmNew ? "text" : "password"}
            margin="normal"
            variant="outlined"
            {...registerPassword("confirmNewPassword")}
            error={!!passwordErrors.confirmNewPassword}
            helperText={passwordErrors.confirmNewPassword?.message}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirmNew(!showConfirmNew)}>
                    {showConfirmNew ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenPasswordDialog(false)}
            sx={{ borderRadius: 2, color: '#666' }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            onClick={handlePasswordSubmit(handlePasswordChange)}
            sx={{ ...modernButtonStyle, bgcolor: '#ff9900', borderRadius: 2 }}
          >
            Update Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentRegistration;
